import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { JobPriceModal } from '../components/JobPriceModal';
import { PriceSummary } from '../components/PriceSummary';
import { useChargeFlags } from '../hooks/useChargeFlags';
import { AccountingBadge } from '../components/AccountingBadge';
import { EmptyState } from '../components/EmptyState';
import { EvidenceStrip } from '../components/EvidenceStrip';
import { ReadFailure } from '../components/ReadFailure';
import { StatusBadge } from '../components/StatusBadge';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Field, TextArea, TextInput, useFieldId } from '../components/ui/Field';
import { Modal } from '../components/ui/Modal';
import { listenDocument, listenQuery } from '../lib/liveRead';
import { db, functions } from '../lib/firebase';
import { formatMoneyPence, formatTimestamp } from '../lib/format';
import { useAuth } from '../auth/AuthProvider';
import { usePageTitle } from '../hooks/usePageTitle';
import { useJobFinanceMap, useJobFinanceStatus, withJobFinance } from '../lib/jobFinance';
import { priceInputPence } from '../lib/priceInput';
import {
  canInvoiceJobs,
  canPriceJobs,
  type Job,
} from '../types/models';

type AccountsJob = Job;

export default function AccountsPage() {
  usePageTitle('Accounts');
  const { activeCompanyId, activeRole, user } = useAuth();
  const authScope = JSON.stringify([user?.uid, activeCompanyId, activeRole]);
  const [queueJobs, setQueueJobs] = useState<AccountsJob[]>([]);
  const [featuresEnabled, setFeaturesEnabled] = useState<boolean | null>(null);
  const [chargesEnabled, setChargesEnabled] = useState(false);
  const [featuresScope, setFeaturesScope] = useState<string | null>(null);
  const openFlags = useChargeFlags(undefined, chargesEnabled && featuresScope === authScope);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);
  const [pricingJob, setPricingJob] = useState<AccountsJob | null>(null);
  const [invoiceJob, setInvoiceJob] = useState<AccountsJob | null>(null);

  const canPrice = canPriceJobs(activeRole ?? undefined);
  const canInvoice = canInvoiceJobs(activeRole ?? undefined);
  const financeStatus = useJobFinanceStatus();

  useEffect(() => {
    setPricingJob(null); setInvoiceJob(null); setChargesEnabled(false); setFeaturesScope(null);
    if (!canPrice && !canInvoice) {
      setQueueJobs([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (!activeCompanyId) {
      setQueueJobs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setQueueJobs([]);
    setFeaturesEnabled(null);
    setFeaturesError(null);
    setPricingJob(null);
    setInvoiceJob(null);

    const unsubFeatures = listenDocument(
      doc(db, 'companies', activeCompanyId),
      (snap) => {
        if (!snap.exists()) {
          setFeaturesError('Company settings not found.');
          return;
        }
        const features = snap.data()?.features as
          | { accountsQueueEnabled?: boolean }
          | undefined;
        setFeaturesEnabled(features?.accountsQueueEnabled !== false);
        setFeaturesScope(authScope);
        setChargesEnabled((snap.data()?.features as {additionalChargesEnabled?: boolean} | undefined)?.additionalChargesEnabled !== false);
        setFeaturesError(null);
      },
      (e) => { setFeaturesEnabled(null); setFeaturesScope(null); setChargesEnabled(false); setFeaturesError(e.message); }
    );

    // PR-26: one query. Completed work stays in the queue after archiving
    // until it is invoiced (exact parity with the old two-collection union).
    const queueQuery = query(
      collection(db, 'companies', activeCompanyId, 'jobs'),
      where('state', 'in', ['completed', 'archived']),
      orderBy('createdAt', 'desc')
    );

    const unsubQueue = listenQuery(
      queueQuery,
      (snap) => {
        setQueueJobs(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Job, 'id'>) }))
        );
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      unsubFeatures();
      unsubQueue();
    };
  }, [authScope, canInvoice, canPrice, readAttempt]);

  // Prices come from the protected price records (lib/jobFinance), merged in here.
  const finance = useJobFinanceMap();
  const jobs = useMemo(
    () =>
      queueJobs
        .map((job) => withJobFinance(job, finance.get(job.id)))
        .filter((job) => job.accountingStatus !== 'invoiced')
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return aTime - bTime;
        }),
    [queueJobs, finance]
  );
  const currentPricingJob = pricingJob ? jobs.find(job => job.id === pricingJob.id) : undefined;
  const currentInvoiceJob = invoiceJob ? jobs.find(job => job.id === invoiceJob.id) : undefined;

  if (!canPrice && !canInvoice) {
    return (
      <EmptyState
        title="Accounts unavailable"
        description="Your role cannot price or invoice completed jobs."
      />
    );
  }

  return (
    <div>
      <h1 className="text-[32px] font-bold leading-[38px] tracking-[-0.03em] text-ink">Accounts</h1>
      <p className="mt-1.5 text-[15px] text-ink2">
        {loading || financeStatus.loading
          ? 'Loading the queue…'
          : error
            ? 'The queue is unavailable.'
            : `${jobs.length} completed job${jobs.length === 1 ? '' : 's'} awaiting a price.`}
      </p>

      <div className="mt-4 space-y-3 empty:hidden">
        {featuresEnabled === false && (
          <Alert tone="warning">
            The Accounts Queue is disabled for this company, so pricing and invoicing are turned off.
            An admin can switch it back on in Company settings.
          </Alert>
        )}
        {error && <ReadFailure message={error} retry={() => setReadAttempt((n) => n + 1)} />}
        {openFlags.error && <ReadFailure message={`Charge flags could not be loaded: ${openFlags.error}`} retry={openFlags.retry} />}
        {featuresError && <ReadFailure message={featuresError} retry={() => setReadAttempt((n) => n + 1)} />}
        {financeStatus.error && <ReadFailure message={`Prices could not be loaded: ${financeStatus.error}`} retry={financeStatus.retry} />}
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {loading || financeStatus.loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-[14px] border border-hair bg-elev p-4">
              <div className="skeleton h-5 w-32 rounded-md" />
              <div className="skeleton mt-3 h-[46px] w-56 rounded-[9px]" />
            </div>
          ))
        ) : error || !financeStatus.ready ? null : jobs.length === 0 ? (
          <EmptyState
            title="Nothing waiting to be priced"
            description="Completed jobs appear here until they are invoiced."
          />
        ) : (
          jobs.map((job) => (
            <QueueEntry
              key={job.id}
              job={job}
              flagCount={openFlags.flags.filter(flag => flag.jobId === job.id).length}
              chargesEnabled={chargesEnabled && featuresScope === authScope}
              companyId={activeCompanyId}
              canPrice={canPrice}
              canInvoice={canInvoice}
              enabled={featuresScope === authScope && featuresEnabled === true && financeStatus.ready}
              onEditNotes={() => setPricingJob(job)}
              onInvoice={() => setInvoiceJob(job)}
            />
          ))
        )}
      </div>

      {currentPricingJob && activeCompanyId && financeStatus.ready && (
        <JobPriceModal key={`${authScope}/${currentPricingJob.id}`} companyId={activeCompanyId} job={currentPricingJob} onClose={() => setPricingJob(null)} />
      )}
      {currentInvoiceJob && activeCompanyId && financeStatus.ready && (
        <InvoiceModal key={`${authScope}/${currentInvoiceJob.id}`} companyId={activeCompanyId} job={currentInvoiceJob} onClose={() => setInvoiceJob(null)} />
      )}
    </div>
  );
}

/**
 * One job waiting to be priced. The evidence sits next to the price because the price
 * is argued from the photographs, and the amount is typed in place rather than behind
 * a dialog — pricing is the repeated action on this screen.
 */
function QueueEntry({
  job, flagCount, chargesEnabled, companyId, canPrice, canInvoice, enabled, onEditNotes, onInvoice,
}: {
  job: AccountsJob;
  flagCount: number;
  chargesEnabled: boolean;
  companyId: string | null;
  canPrice: boolean;
  canInvoice: boolean;
  enabled: boolean;
  onEditNotes: () => void;
  onInvoice: () => void;
}) {
  const [draftAmount, setDraftAmount] = useState<string | null>(null);
  const amount = draftAmount ?? (job.pricing ? (job.pricing.amountPence / 100).toFixed(2) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const priced = !!job.pricing;

  async function savePrice() {
    if (!companyId || !enabled || !canPrice || saving) return;
    const amountPence = priceInputPence(amount);
    if (amountPence === null) { setError('Enter a valid price.'); return; }
    setSaving(true);
    setError(null);
    try {
      const call = httpsCallable<
        { companyId: string; jobId: string; amountPence: number; currency: string; notes?: string },
        { success: boolean }
      >(functions, 'saveJobPricing');
      // Omit breakdown: a stale quick save must be refused if charge lines were added.
      await call({
        companyId,
        jobId: job.id,
        amountPence,
        currency: job.pricing?.currency || 'GBP',
        notes: job.pricing?.notes || undefined,
      });
      setDraftAmount(null);
    } catch (e) {
      console.error(e);
      setError(friendlyCallableError(e));
    } finally {
      setSaving(false);
    }
  }

  const jobHref = `/jobs/archived/${job.id}`;
  const unchanged = amount.trim() === (job.pricing ? (job.pricing.amountPence / 100).toFixed(2) : '');

  return (
    <article className="rounded-[14px] border border-hair bg-elev px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="min-w-[180px] flex-1">
          <Link to={jobHref} className="reg text-[17px] text-ink hover:text-accent">
            {job.registrationNumber || 'No registration'}
          </Link>
          <p className="mt-0.5 truncate text-[13.5px] text-ink2">
            {job.customerName || 'No customer'}
            {[job.make, job.model].filter(Boolean).length > 0 && <> · {[job.make, job.model].filter(Boolean).join(' ')}</>}
          </p>
          <p className="text-[12.5px] text-ink3">
            Completed <span className="font-mono">{formatTimestamp(job.completedAt ?? job.clientCreatedAt ?? job.createdAt)}</span>
          </p>
        </div>

        {/* The evidence sits between the job and its price, because the price is
            argued from the photographs — and each one opens the job. */}
        <EvidenceStrip job={job} href={jobHref} max={4} size={40} />

        <div className="flex flex-wrap items-center gap-2">
          <label className="w-[150px]">
            <span className="sr-only">Price for {job.registrationNumber || 'this job'}</span>
            <span className="relative block">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[15px] text-ink3">£</span>
              <TextInput
                value={amount}
                onChange={(event) => { setDraftAmount(event.target.value); setError(null); }}
                inputMode="decimal"
                placeholder="0.00"
                disabled={!canPrice || !enabled || saving || !!job.pricing?.breakdown?.charges.length}
                aria-label={`Price for ${job.registrationNumber || 'this job'}`}
                className="h-10 pl-7 font-mono"
              />
            </span>
          </label>
          {canPrice && (
            <Button type="button" size="sm" onClick={job.pricing?.breakdown?.charges.length ? onEditNotes : savePrice} loading={saving}
              disabled={!enabled || (!job.pricing?.breakdown?.charges.length && (!amount.trim() || unchanged))}>
              {job.pricing?.breakdown?.charges.length ? 'Edit price' : priced ? 'Save' : 'Add price'}
            </Button>
          )}
          {canPrice && (
            <Button type="button" variant="ghost" size="sm" onClick={onEditNotes} disabled={!enabled}>
              {job.pricing?.notes ? 'Notes' : 'Add notes'}
            </Button>
          )}
          {canInvoice && (
            // Disabled until there is a price, rather than renamed to explain itself.
            <Button type="button" variant="secondary" size="sm" onClick={onInvoice}
              disabled={!enabled || job.accountingStatus !== 'priced'}
              title={job.accountingStatus !== 'priced' ? 'Add a price first' : undefined}>
              Mark invoiced
            </Button>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <AccountingBadge status={job.accountingStatus} size="sm" />
          <StatusBadge status={job.state} size="sm" />
        </div>
      </div>
      {canPrice && chargesEnabled && <Button type="button" size="sm" variant="ghost" onClick={onEditNotes} disabled={!enabled}>Breakdown</Button>}
      {flagCount > 0 && <p className="mt-2 text-sm text-ink2">{flagCount} driver flagged</p>}
      {job.pricing?.notes && <p className="mt-2 text-[12.5px] text-ink3">{job.pricing.notes}</p>}
      {error && <p className="mt-2 text-[13px] text-st-cancelled">{error}</p>}
    </article>
  );
}

function InvoiceModal({
  companyId,
  job,
  onClose,
}: {
  companyId: string;
  job: AccountsJob;
  onClose: () => void;
}) {
  const [reference, setReference] = useState('');
  const {user, activeCompanyId, activeRole} = useAuth();
  const mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const financeStatus = useJobFinanceStatus();

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!user || activeCompanyId !== companyId || !canInvoiceJobs(activeRole ?? undefined) || job.accountingStatus !== 'priced' || !financeStatus.ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      const call = httpsCallable<
        { companyId: string; jobId: string; invoiceReference?: string },
        { success: boolean }
      >(functions, 'markJobInvoiced');
      await call({
        companyId,
        jobId: job.id,
        invoiceReference: reference.trim() || undefined,
      });
      if (mounted.current) onClose();
    } catch (e) {
      console.error(e);
      if (mounted.current) setError(friendlyCallableError(e));
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Mark invoiced"
      description="The job leaves the queue and its price is locked.">
      <form onSubmit={save} className="space-y-4">
        <PriceSummary pricing={job.pricing} />
        <p className="text-[13px] text-ink2">Marking it invoiced locks the price. A Super Admin, Admin or Manager can unlock it.</p>
        <Field label="Invoice reference" optional>
          <TextInput value={reference} onChange={(event) => setReference(event.target.value)}
            placeholder="Your invoice number" autoFocus className="font-mono" />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={saving} disabled={!financeStatus.ready}>Mark invoiced</Button>
        </div>
      </form>
    </Modal>
  );
}

function friendlyCallableError(error: unknown) {
  const raw = error instanceof Error ? error.message : String(error);
  const message = raw.replace(/^FirebaseError:\s*/i, '');
  if (
    message.toLowerCase().includes('app check') ||
    message.toLowerCase().includes('appcheck') ||
    message.toLowerCase().includes('unauthenticated')
  ) {
    return 'Firebase App Check blocked this request. Hard refresh once, then try again. If it continues, the deployed web App Check key/domain needs checking.';
  }
  if (message.toLowerCase().includes('accounts queue is disabled')) {
    return 'The Accounts Queue is disabled in Company Settings.';
  }
  if (message.toLowerCase().includes('only completed jobs')) {
    return 'Only completed jobs can be priced or invoiced.';
  }
  return message || 'The accounts action failed.';
}
